import { type FC } from "react";

import { Check } from "lucide-react";

import { cn } from "@/shared/utils/core";

interface StepperItemProps {
  stepNumber: number;
  label: string;
  status: "completed" | "active" | "upcoming";
}

export const StepperItem: FC<StepperItemProps> = ({ stepNumber, label, status }) => {
  const isCompleted = status === "completed";
  const isActive = status === "active";
  const isUpcoming = status === "upcoming";

  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-lg p-3",
        isActive && "bg-brand-subtle border border-brand",
      )}
      {...(isActive ? { "aria-current": "step" as const } : {})}
    >
      <div
        className={cn(
          "flex h-6 w-6 shrink-0 items-center justify-center rounded-full",
          isCompleted && "bg-brand",
          isActive && "bg-brand",
          isUpcoming && "bg-background",
        )}
      >
        {isCompleted ? (
          <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} aria-label="Completed" />
        ) : (
          <span
            className={cn(
              "text-sm font-semibold leading-none",
              isActive && "text-white",
              isUpcoming && "text-tertiary",
            )}
          >
            {stepNumber}
          </span>
        )}
      </div>
      <p
        className={cn(
          "flex-1 min-w-0 text-sm",
          isCompleted && "text-secondary",
          isActive && "font-medium text-primary",
          isUpcoming && "text-secondary",
        )}
      >
        {label}
      </p>
    </div>
  );
};

export function getStepStatus(
  step: number,
  activeStep: number,
): "completed" | "active" | "upcoming" {
  if (step < activeStep) return "completed";
  if (step === activeStep) return "active";
  return "upcoming";
}
